import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package,
  ShieldCheck,
  Tag,
  FlaskConical,
  Boxes,
  Warehouse,
  Truck,
  CircleDollarSign,
  Info,
  ArrowRight,
  AlertCircle,
  Snowflake,
  FileCheck2,
  Layers,
  DollarSign,
  Users,
} from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import BrandMark from "@/components/BrandMark";

/**
 * Post-login Chooser — buyers pick their tier before entering the shop.
 * The two tier cards use DISTINCT palettes so buyers can't accidentally
 * confuse Supply for Select:
 *   • Supply — bone / charcoal / oxblood (matches the Supply catalog itself)
 *   • Select — dark charcoal / brass-gold (matches the R-1617 sub-brand)
 * Both cards sit on a warm neutral background so both palettes read cleanly.
 */

const GOLD = "#C9A24B";
const NEUTRAL_BG = "#EBE3D3"; // warm neutral — between bone and taupe
const OXBLOOD = "#4A0E17";

export default function Chooser() {
  const { logout, user } = useAuth();
  const loc = useLocation();
  const fromDeepLink = !!loc.state?.fromDeepLink;

  return (
    <div
      className="min-h-screen flex flex-col relative overflow-hidden"
      style={{ background: NEUTRAL_BG, color: "#1A1A1A" }}
      data-testid="chooser-page"
    >
      {/* Subtle paper grain — same overlay, lower opacity on the light bg */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
        aria-hidden
      />

      {/* Header — matches the standard site Nav (BrandMark badge + wordmark).
          Sign-in-as + Sign out live on the right so the buyer still has an
          exit hatch from the pre-catalog Chooser page. */}
      <header className="relative border-b border-charcoal/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <BrandMark variant="dark" size="sm" asLink={false} />
          <div className="flex items-center gap-4 sm:gap-6">
            {user?.name && (
              <span className="hidden sm:inline text-[12px] uppercase tracking-[0.28em] text-charcoal/85 font-mono">
                Signed in as {user.name.split(" ")[0]}
              </span>
            )}
            <button
              type="button"
              onClick={logout}
              className="text-[13px] uppercase tracking-[0.22em] text-charcoal/80 hover:text-oxblood transition-colors"
              data-testid="chooser-signout"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="relative flex-1 py-8 sm:py-20 lg:py-24">
        <div className="w-full max-w-[1200px] mx-auto px-5 sm:px-6 lg:px-12">
          {/* iter148 — Subtle deep-link nudge: shown when a customer arrived here
              from a bookmarked protected URL (e.g. /shop/xxx). Reinforces the
              intentional tier choice without being modal or blocking. */}

          {fromDeepLink && (
            <div
              className="mb-8 sm:mb-10 mx-auto max-w-2xl rounded-md border-l-2 border-oxblood/70 bg-white/40 px-4 py-3 text-center animate-fade-up"
              data-testid="chooser-deeplink-nudge"
            >
              <p className="text-[11px] uppercase tracking-[0.28em] font-mono text-oxblood mb-1">
                One quick step
              </p>
              <p className="text-[14px] text-charcoal/85 leading-relaxed">
                Please pick your tier to continue.
              </p>
            </div>
          )}

          {/* Intro block */}
          <div className="text-center max-w-2xl mx-auto animate-fade-up">
            <h1 className="mt-4 sm:mt-6 font-serif text-[32px] sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] uppercase text-charcoal">
              Which is right <span className="italic normal-case text-charcoal/85">for you?</span>
            </h1>
            <Divider />
          </div>

          {/* Cards */}
          <div className="mt-4 sm:mt-8 grid md:grid-cols-3 gap-5 sm:gap-6 lg:gap-8 items-stretch animate-fade-up [animation-delay:120ms]">
            <div className="relative h-full">
              <TierCard
                testid="chooser-supply"
                variant="supply"
                to="/shop?tier=supply"
                comingSoon
                headerIcon={Package}
                title="Research Supply"
                subtitle="Built for value & simplicity"
                cta="Shop Research Supply"
                hoverTitle="You're heading to Research SUPPLY"
                hoverBody="Tier-based pricing · 3-vial kits · 3rd-party tested (purity · quantity · identity) · one kit minimum. Click again to continue."
                features={[
                  { icon: Tag, label: "Tier-Based Pricing" },
                  { icon: FlaskConical, label: "3-Vial Kits" },
                  {
                    icon: ShieldCheck,
                    label: "3rd-Party Tested",
                    sub: "Purity · Quantity · Identity",
                  },
                  { icon: Boxes, label: "One Kit Minimum" },
                ]}
              />
              <ComingSoonSticker testid="chooser-supply-coming-sticker" rotation={-5} />
            </div>
            <TierCard
              testid="chooser-select"
              variant="select"
              to="/select"
              headerIcon={ShieldCheck}
              title="Research Select"
              subtitle="Built for choice & flexibility"
              cta="Shop Research Select"
              hoverTitle="You're heading to Research SELECT"
              hoverBody="US Lyophilized · single, 3 & 5-vial options · 3rd-party tested (purity · quantity · identity · endotoxins) · mix & match available. Click again to continue."
              features={[
                { icon: Snowflake, label: "US Lyophilized" },
                { icon: FlaskConical, label: "Single, 3 & 5-Vial Options" },
                {
                  icon: ShieldCheck,
                  label: "3rd-Party Tested",
                  sub: "Purity · Quantity · Identity · Endotoxins",
                },
                { icon: Boxes, label: "Mix & Match Available" },
              ]}
            />
            <div className="relative h-full">
              <TenVKCard disabled />
              <ComingSoonSticker testid="chooser-10vk-coming-sticker" rotation={4} />
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

/**
 * 10VK Chooser Card — locked clinical palette (white / black / Manrope).
 * Visually distinct from SUPPLY (oxblood) and SELECT (gold). Does not reuse
 * TierCard to prevent cross-contamination with SUPPLY/SELECT styling.
 */
function TenVKCard({ disabled = false }) {
  const features = [
    { icon: Layers,        label: "10-Vial Kits",              sub: "Each kit includes 10 vials of the same peptide" },
    { icon: FlaskConical,  label: "Untested",                  sub: "For independent research testing" },
    { icon: DollarSign,    label: "Direct Pricing",            sub: "Our most cost-effective option" },
    { icon: Users,         label: "For Experienced Researchers", sub: "Ideal for those who arrange their own testing" },
  ];
  const Wrapper = disabled ? "div" : Link;
  const wrapperProps = disabled ? { "aria-disabled": "true" } : { to: "/shop/10-vial" };
  // Structural shell mirrors TierCard exactly (same paddings / section
  // sizes / CTA dimensions) so SUPPLY, SELECT, and 10VK are structurally
  // identical in width, height, and CTA placement. Internal 10VK design
  // (white/black Manrope clinical) is preserved.
  const headerBorderColor = "rgba(0,0,0,0.12)";
  const featureBorderColor = "rgba(0,0,0,0.08)";
  return (
    <Wrapper
      {...wrapperProps}
      data-testid="chooser-10vk"
      className={`group relative rounded-[6px] overflow-hidden flex flex-col h-full transition-all duration-500 ${
        disabled ? "cursor-not-allowed" : "hover:-translate-y-[2px]"
      }`}
      style={{
        background: "#FFFFFF",
        border: "1px solid #000000",
        textDecoration: "none",
        fontFamily: "Manrope, system-ui, sans-serif",
        color: "#000000",
      }}
    >
      {/* Header — matches TierCard header dimensions exactly: same outer
          padding, same gap, same icon-box footprint (44 mobile / 56 desktop),
          same inner icon size, same title + subtitle type scale and
          spacing. 10VK retains Manrope + no decorative hexagon frame. */}
      <div
        className="px-5 sm:px-9 pt-3 sm:pt-10 pb-4 sm:pb-6 flex items-center gap-3 sm:gap-4"
        style={{ borderBottom: `1px solid ${headerBorderColor}` }}
      >
        <span className="sm:hidden inline-grid place-items-center shrink-0" style={{ width: 44, height: 44 }} aria-hidden>
          <Package size={18} strokeWidth={1.6} color="#000000" />
        </span>
        <span className="hidden sm:inline-grid place-items-center shrink-0" style={{ width: 56, height: 56 }} aria-hidden>
          <Package size={22} strokeWidth={1.6} color="#000000" />
        </span>
        <div>
          <h2
            className="text-[22px] sm:text-[32px] lg:text-[38px] uppercase tracking-tight leading-none"
            style={{ color: "#000000", fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 700, whiteSpace: "pre-line" }}
          >
            {"10-Vial\nKits"}
          </h2>
          <p
            className="mt-1.5 sm:mt-2 text-[11px] sm:text-[13px] uppercase tracking-[0.24em]"
            style={{ color: "rgba(0,0,0,0.70)", fontFamily: "Manrope, system-ui, sans-serif", fontWeight: 500 }}
          >
            Untested · Direct Pricing
          </p>
        </div>
      </div>

      {/* Feature list — matches TierCard list padding + per-row spacing */}
      <ul className="px-5 sm:px-9 py-1 sm:py-2 flex-1">
        {features.map((f, i) => {
          const Icon = f.icon;
          return (
            <li
              key={i}
              className="flex items-center gap-3 sm:gap-4 py-2 sm:py-3.5"
              style={{ borderBottom: i === features.length - 1 ? "none" : `1px solid ${featureBorderColor}` }}
            >
              <span
                className="inline-grid place-items-center shrink-0"
                style={{ width: 28, height: 28 }}
                aria-hidden
              >
                <Icon size={16} strokeWidth={1.6} color="#000000" />
              </span>
              <span
                className="text-[13px] sm:text-[15px] leading-tight"
                style={{ color: "#000000", fontFamily: "Manrope, system-ui, sans-serif" }}
              >
                {f.label}
                {f.sub && (
                  <span
                    className="block mt-0.5 text-[11px] sm:text-[12px] leading-snug"
                    style={{ color: "rgba(0,0,0,0.70)", fontFamily: "Manrope, system-ui, sans-serif" }}
                  >
                    {f.sub}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {/* CTA — matches TierCard CTA slot and inner CTA dimensions exactly */}
      <div className="px-5 sm:px-9 pt-2 pb-5 sm:pb-9">
        <div
          className="w-full flex items-center justify-center gap-2.5 py-3 sm:py-3.5 uppercase tracking-[0.22em] text-[12px] sm:text-[13px] font-mono rounded-full"
          style={{
            background: "#000000",
            color: "#FFFFFF",
            border: "1px solid #000000",
            opacity: disabled ? 0.6 : 1,
          }}
          data-testid="chooser-10vk-cta"
          aria-disabled={disabled ? "true" : undefined}
        >
          Shop 10-Vial Kits
          <ArrowRight size={14} strokeWidth={2.4} />
        </div>
      </div>
    </Wrapper>
  );
}

/**
 * ComingSoonSticker — handwritten paper-note style overlay. Absolutely
 * positioned to the top-right of a `relative` parent. Intentionally slightly
 * rotated and off-polished to feel like a temporary paper sticker.
 */
function ComingSoonSticker({ testid, rotation = -5 }) {
  return (
    <div
      data-testid={testid}
      aria-hidden
      className="pointer-events-none select-none"
      style={{
        position: "absolute",
        top: "-14px",
        right: "22px",
        transform: `rotate(${rotation}deg)`,
        zIndex: 20,
        background: "#FFF8E1",
        color: "#1A1A1A",
        padding: "10px 16px 8px",
        fontFamily: '"Caveat", "Permanent Marker", "Marker Felt", "Comic Sans MS", cursive',
        fontSize: "22px",
        fontWeight: 700,
        letterSpacing: "0.04em",
        lineHeight: 1,
        boxShadow: "0 6px 14px -6px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.15)",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: "1px",
      }}
    >
      {/* tape bar */}
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: "-9px",
          left: "50%",
          transform: "translateX(-50%) rotate(-2deg)",
          width: "38px",
          height: "14px",
          background: "rgba(200,195,170,0.55)",
          borderLeft: "1px solid rgba(0,0,0,0.08)",
          borderRight: "1px solid rgba(0,0,0,0.08)",
        }}
      />
      coming soon!
    </div>
  );
}

/* ------------------------------- Sub-parts ------------------------------- */

function TierCard({ headerIcon: HeaderIcon, title, subtitle, features, cta, to, testid, comingSoon, recommended, variant, hoverTitle, hoverBody }) {
  const isSupply = variant === "supply";
  const accent = isSupply ? OXBLOOD : GOLD;

  const cardStyle = isSupply
    ? {
        background: "#F5EFE5",
        border: "1px solid rgba(26,26,26,0.14)",
        boxShadow: "0 30px 80px -40px rgba(0,0,0,0.35)",
      }
    : {
        background: "#F1E5C7",
        border: "1px solid rgba(26,26,26,0.14)",
        boxShadow: "0 30px 80px -40px rgba(0,0,0,0.35)",
      };

  const featureBorderColor = "rgba(26,26,26,0.08)";
  const headerBorderColor = "rgba(26,26,26,0.10)";
  const titleColor = "#1A1A1A";
  const featureTextColor = "text-charcoal";

  const Wrapper = comingSoon ? "div" : Link;
  const wrapperProps = comingSoon
    ? { "aria-disabled": "true" }
    : { to };

  return (
    <Wrapper
      {...wrapperProps}
      data-testid={testid}
      className={`group relative rounded-[6px] overflow-hidden flex flex-col h-full transition-all duration-500 ${
        comingSoon ? "cursor-not-allowed" : "hover:-translate-y-[2px]"
      }`}
      style={cardStyle}
    >
      {/* Ribbon: `recommended` only. The `comingSoon` gold pill was removed —
          the handwritten COMING SOON sticker in Chooser now serves that role. */}
      {recommended && (
        <div className="flex justify-end px-5 pt-3 sm:px-0 sm:pt-0 sm:absolute sm:top-5 sm:right-5 z-10">
          <span
            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] font-mono rounded-[3px] flex items-center gap-1.5"
            style={{
              background: "transparent",
              color: "#1A1A1A",
              border: `1px solid ${OXBLOOD}`,
            }}
            data-testid="chooser-supply-recommended"
          >
            <span
              className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: OXBLOOD }}
              aria-hidden
            />
            Recommended for Labs
          </span>
        </div>
      )}

      {/* Top accent hairline on hover */}
      <span
        className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }}
        aria-hidden
      />

      {/* Header */}
      <div
        className="px-5 sm:px-9 pt-3 sm:pt-10 pb-4 sm:pb-6 flex items-center gap-3 sm:gap-4"
        style={{ borderBottom: `1px solid ${headerBorderColor}` }}
      >
        <span className="sm:hidden">
          <Hexagon size={44} accent={accent} tint={isSupply ? "rgba(74,14,23,0.06)" : "rgba(201,162,75,0.06)"}>
            <HeaderIcon size={18} strokeWidth={1.6} style={{ color: accent }} />
          </Hexagon>
        </span>
        <span className="hidden sm:inline">
          <Hexagon size={56} accent={accent} tint={isSupply ? "rgba(74,14,23,0.06)" : "rgba(201,162,75,0.06)"}>
            <HeaderIcon size={22} strokeWidth={1.6} style={{ color: accent }} />
          </Hexagon>
        </span>
        <div>
          <h2 className="font-serif text-[22px] sm:text-[32px] lg:text-[38px] uppercase tracking-tight leading-none" style={{ color: titleColor }}>
            {title}
          </h2>
          <p className="mt-1.5 sm:mt-2 text-[11px] sm:text-[13px] uppercase tracking-[0.24em] font-mono" style={{ color: accent }}>
            {subtitle}
          </p>
        </div>
      </div>

      {/* Feature list — one per row */}
      <ul className="px-5 sm:px-9 py-1 sm:py-2 flex-1">
        {features.map((f, i) => (
          <li
            key={i}
            className="flex items-center gap-3 sm:gap-4 py-2 sm:py-3.5"
            style={{ borderBottom: i === features.length - 1 ? "none" : `1px solid ${featureBorderColor}` }}
          >
            <Hexagon size={28} accent={accent} tint={isSupply ? "rgba(74,14,23,0.06)" : "rgba(201,162,75,0.06)"}>
              <f.icon size={12} strokeWidth={1.8} style={{ color: accent }} />
            </Hexagon>
            <span className={`text-[13px] sm:text-[15px] leading-tight ${featureTextColor}`}>
              {f.label}
              {f.sub && (
                <span className={`block mt-0.5 text-[11px] sm:text-[12px] leading-snug ${featureTextColor} opacity-70`}>
                  {f.sub}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA — wrapped in HoverCard so hover on desktop shows a tier-confirmation
          popover. Mobile devices don't fire hover, so we ALSO render a small
          always-visible subtitle underneath (mobile-safety-net) with a short
          tier reminder — the whole reason this component exists is to stop
          buyers from picking the wrong tier by accident.
          When `comingSoon`, the CTA stays visible at identical dimensions
          (for cross-card parity on the chooser) but is non-navigating. */}
      <div className="px-5 sm:px-9 pt-2 pb-5 sm:pb-9">
        {comingSoon ? (
          <div
            className="w-full flex items-center justify-center gap-2.5 py-3 sm:py-3.5 uppercase tracking-[0.22em] text-[12px] sm:text-[13px] font-mono rounded-full"
            style={
              isSupply
                ? { background: OXBLOOD, color: "#F5EFE5", border: `1px solid ${OXBLOOD}`, opacity: 0.6 }
                : { background: GOLD, color: "#0F0E0C", border: `1px solid ${GOLD}`, opacity: 0.6 }
            }
            data-testid={`${testid}-cta`}
            aria-disabled="true"
          >
            {cta}
            <ArrowRight size={14} strokeWidth={2.4} />
          </div>
        ) : (
          <>
            <HoverCard openDelay={80} closeDelay={80}>
              <HoverCardTrigger asChild>
                <div
                  className="w-full flex items-center justify-center gap-2.5 py-3 sm:py-3.5 uppercase tracking-[0.22em] text-[12px] sm:text-[13px] font-mono rounded-full transition-all group-hover:brightness-[1.08]"
                  style={
                    isSupply
                      ? { background: OXBLOOD, color: "#F5EFE5", border: `1px solid ${OXBLOOD}` }
                      : { background: GOLD, color: "#0F0E0C", border: `1px solid ${GOLD}` }
                  }
                  data-testid={`${testid}-cta`}
                >
                  {cta}
                  <ArrowRight size={14} strokeWidth={2.4} />
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                side="top"
                align="center"
                className="w-[380px] border shadow-xl rounded-2xl p-6 hidden md:block"
                style={
                  isSupply
                    ? { background: "#F5EFE5", borderColor: "rgba(74,14,23,0.35)", color: "#1A1A1A" }
                    : { background: "#171512", borderColor: "rgba(201,162,75,0.45)", color: "#F5EFE5" }
                }
                data-testid={`${testid}-hover`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className="mt-0.5 shrink-0 grid place-items-center w-10 h-10 rounded-full"
                    style={
                      isSupply
                        ? { background: "rgba(74,14,23,0.10)", border: `1px solid ${OXBLOOD}` }
                        : { background: "rgba(201,162,75,0.15)", border: `1px solid ${GOLD}` }
                    }
                  >
                    <AlertCircle size={20} strokeWidth={2} style={{ color: accent }} />
                  </div>
                  <div>
                    <p className="text-[13px] font-mono uppercase tracking-[0.28em] mb-3" style={{ color: accent }}>
                      Confirm your tier
                    </p>
                    <p className="text-[22px] leading-tight font-serif font-medium mb-2">{hoverTitle}</p>
                    <p className={`text-[18px] leading-relaxed ${isSupply ? "text-charcoal/85" : "text-bone/85"}`}>
                      {hoverBody}
                    </p>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
            {/* Mobile-safety-net: always-visible tier reminder below the CTA.
                Hidden on ≥md where the hover popover does the same job. */}
            <p
              className="mt-3 sm:mt-4 md:hidden text-[13px] leading-snug text-center"
              style={{ color: isSupply ? "rgba(26,26,26,0.85)" : "rgba(245,239,229,0.85)" }}
              data-testid={`${testid}-mobile-hint`}
            >
              {hoverBody}
            </p>
          </>
        )}
      </div>
    </Wrapper>
  );
}

function Hexagon({ size = 48, accent = GOLD, tint = "rgba(201,162,75,0.06)", children }) {
  const s = size;
  return (
    <span
      className="relative inline-grid place-items-center shrink-0"
      style={{ width: s, height: s }}
      aria-hidden
    >
      <svg width={s} height={s} viewBox="0 0 100 100" className="absolute inset-0">
        <polygon
          points="50,4 92,27 92,73 50,96 8,73 8,27"
          fill={tint}
          stroke={accent}
          strokeWidth="2.5"
        />
      </svg>
      <span className="relative">{children}</span>
    </span>
  );
}

function Divider() {
  return (
    <div className="mt-8 flex items-center justify-center gap-4" aria-hidden>
      <span className="h-px w-16 sm:w-24" style={{ background: `linear-gradient(90deg, transparent, ${OXBLOOD})` }} />
      <span
        className="grid place-items-center font-serif text-[10px] tracking-[0.18em]"
        style={{ width: 34, height: 34, color: OXBLOOD }}
      >
        <svg viewBox="0 0 100 100" width="34" height="34">
          <polygon
            points="50,4 92,27 92,73 50,96 8,73 8,27"
            fill="none"
            stroke={OXBLOOD}
            strokeWidth="2.5"
          />
          <text
            x="50"
            y="58"
            textAnchor="middle"
            fontFamily="serif"
            fontSize="22"
            fill={OXBLOOD}
            fontStyle="italic"
          >
            b
          </text>
        </svg>
      </span>
      <span className="h-px w-16 sm:w-24" style={{ background: `linear-gradient(-90deg, transparent, ${OXBLOOD})` }} />
    </div>
  );
}

function InfoStrip({ icon: Icon, title, body, testid }) {
  return (
    <div
      data-testid={testid}
      className="mx-auto max-w-3xl rounded-[6px] px-5 sm:px-7 py-4 flex items-start gap-4"
      style={{
        background: "#F5EFE5",
        border: "1px solid rgba(26,26,26,0.10)",
      }}
    >
      <span
        className="grid place-items-center shrink-0 mt-0.5"
        style={{ width: 34, height: 34 }}
        aria-hidden
      >
        <Icon size={18} strokeWidth={1.8} style={{ color: OXBLOOD }} />
      </span>
      <div>
        <p className="text-sm sm:text-[15px] text-charcoal font-medium leading-snug">{title}</p>
        {body && <p className="mt-1 text-xs sm:text-sm text-charcoal/85 leading-relaxed">{body}</p>}
      </div>
    </div>
  );
}

/* Custom US flag disc icon (avoids emoji, works as a lucide-style component). */
function UsFlag({ size = 18 }) {
  const s = size;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" aria-hidden>
      <defs>
        <clipPath id="usflag-clip">
          <circle cx="12" cy="12" r="11" />
        </clipPath>
      </defs>
      <g clipPath="url(#usflag-clip)">
        <rect x="0" y="0" width="24" height="24" fill="#F5EFE5" />
        {[1, 3, 5, 7, 9, 11].map((y) => (
          <rect key={y} x="0" y={y * 2} width="24" height="2" fill="#B22234" />
        ))}
        <rect x="0" y="0" width="11" height="12" fill="#3C3B6E" />
      </g>
      <circle cx="12" cy="12" r="11" fill="none" stroke={OXBLOOD} strokeWidth="1.4" />
    </svg>
  );
}
